import React from "react";
import AdminLayout from "../../LayoutWrapper/AdminLayout";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import SEO from "../../Components/SEO";
import GooglePlay from "../../Images/Homepage/google-play.png";
import AppStore from "../../Images/Homepage/app-store.png";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import SupportAgentOutlinedIcon from "@mui/icons-material/SupportAgentOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import StarOutlinedIcon from "@mui/icons-material/StarOutlined";
import "../../CSS/Homepage/modern.css";

const STATS = [
  { value: "24/7", label: "Premium service" },
  { value: "100+", label: "Professional drivers" },
  { value: "5★", label: "Client satisfaction" },
  { value: "VIP", label: "Luxury transfers" },
];

const Homepage = () => {
  const { t } = useTranslation();

  const homeSchema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": "https://comfort-trip.com/#organization",
        name: "Comfort Trip",
        url: "https://comfort-trip.com/",
        logo: "https://comfort-trip.com/logo512.png",
      },
      {
        "@type": "WebSite",
        "@id": "https://comfort-trip.com/#website",
        url: "https://comfort-trip.com/",
        name: "Comfort Trip",
        publisher: {
          "@id": "https://comfort-trip.com/#organization",
        },
      },
      {
        "@type": "WebPage",
        "@id": "https://comfort-trip.com/#webpage",
        url: "https://comfort-trip.com/",
        name: "Comfort Trip | Personal Driver & Chauffeur Service",
        description:
          "Book professional personal drivers with Comfort Trip for safe, reliable and comfortable transportation.",
        isPartOf: {
          "@id": "https://comfort-trip.com/#website",
        },
        about: {
          "@id": "https://comfort-trip.com/#organization",
        },
      },
    ],
  };
  
  return (
    <AdminLayout>
  
      <SEO
        title="Comfort Trip | Personal Driver & Chauffeur Service"
        description="Book professional personal drivers with Comfort Trip. Enjoy safe, reliable and comfortable transportation for daily travel, airport transfers and more."
        canonical="/"
      />
  
      <div className="home-page">
        <section className="home-hero">
          <span className="home-hero__blob home-hero__blob--1" aria-hidden="true" />
          <span className="home-hero__blob home-hero__blob--2" aria-hidden="true" />
          <div className="home-hero__inner">
            <div className="home-hero__content">
              <span className="home-hero__badge">
                <DirectionsCarFilledOutlinedIcon sx={{ fontSize: 18 }} />
                Comfort Trip
              </span>
              <h1 className="home-hero__title">{t("page_title_home")}</h1>
              <p className="home-hero__subtitle">{t("homepage_about_para1")}</p>
              <div className="home-hero__actions">
                <Link to="/user-signup" className="home-hero__btn home-hero__btn--primary">
                  {t("home_banner_btn1")}
                  <ArrowForwardIcon fontSize="small" />
                </Link>
                <Link to="/driver-signup" className="home-hero__btn home-hero__btn--outline">
                  {t("home_banner_btn2")}
                </Link>
              </div>
              <p className="home-hero__stores-label">{t("home_dow_app_text")}</p>
              <div className="home-hero__stores">
                <img src={AppStore} alt="Download on the App Store" />
                <img src={GooglePlay} alt="Get it on Google Play" />
              </div>
            </div>
            <div className="home-hero__visual">
              <div className="home-hero__image" role="img" aria-label="Luxury personal driver" />
              <div className="home-hero__card">
                <div className="home-hero__card-icon">
                  <StarOutlinedIcon />
                </div>
                <div className="home-hero__card-text">
                <strong>Professional Drivers</strong>
                <span>Safe, comfortable rides when you need them</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="home-stats" aria-label="Highlights">
          <div className="home-stats__inner">
            {STATS.map((stat) => (
              <div key={stat.label} className="home-stats__item">
                <div className="home-stats__value">{stat.value}</div>
                <div className="home-stats__label">{stat.label}</div>
              </div>
            ))}
          </div>
        </section>

        <section className="home-invest">
          <div className="home-invest__inner">
            <h2 className="home-invest__title">{t("Invest_and_Earn")}</h2>
            <p className="home-invest__text">
            Partner with Comfort Trip and grow with a platform built for professional personal driver and chauffeur services.
            </p>
            <Link to="/contact" className="home-invest__btn">
              Learn more
              <ArrowForwardIcon fontSize="small" />
            </Link>
          </div>
        </section>

        <section className="home-estimate">
          <div className="home-estimate__inner">
            <h2 className="home-estimate__title">{t("Get_an_estimate")}</h2>
            <form
              className="home-estimate__form"
              onSubmit={(e) => e.preventDefault()}
            >
              <div className="home-estimate__input-wrap">
                <PlaceOutlinedIcon className="home-estimate__input-icon" />
                <input
                  type="text"
                  className="home-estimate__input"
                  placeholder={t("Pick_up_Point")}
                  aria-label={t("Pick_up_Point")}
                />
              </div>
              <div className="home-estimate__input-wrap">
                <FlagOutlinedIcon className="home-estimate__input-icon" />
                <input
                  type="text"
                  className="home-estimate__input"
                  placeholder={t("Drop_Point")}
                  aria-label={t("Drop_Point")}
                />
              </div>
              <button type="submit" className="home-estimate__submit">
                {t("Get_Estimate")}
              </button>
            </form>
          </div>
        </section>

        <section className="home-drive">
          <div className="home-drive__inner">
            <div
              className="home-drive__image"
              role="img"
              aria-label="Drive with Comfort Trip"
            />
            <div className="home-drive__content">
              <p className="home-drive__eyebrow">{t("applyDrive_title1")}</p>
              <h2 className="home-drive__title">{t("applyDrive_title2")}</h2>
              <div className="home-drive__feature">
                <div className="home-drive__feature-icon">
                  <SupportAgentOutlinedIcon />
                </div>
                <div>
                  <h3 className="home-drive__feature-title">
                    {t("applyDrive_subtitle1")}
                  </h3>
                  <p className="home-drive__feature-text">{t("applyDrive_para1")}</p>
                </div>
              </div>
              <div className="home-drive__feature">
                <div className="home-drive__feature-icon">
                  <SecurityOutlinedIcon />
                </div>
                <div>
                  <h3 className="home-drive__feature-title">
                    {t("applyDrive_subtitle2")}
                  </h3>
                  <p className="home-drive__feature-text">{t("applyDrive_para2")}</p>
                </div>
              </div>
              <Link to="/driver-signup" className="home-drive__btn">
                {t("applyDrive_btn")}
                <ArrowForwardIcon fontSize="small" />
              </Link>
            </div>
          </div>
        </section>

        <section className="home-about">
          <div className="home-about__inner">
            <p className="home-about__label">Who we are</p>
            <h2 className="home-about__title">{t("homepage_about_title")}</h2>
            <p className="home-hero__subtitle">{t("home_hero_description")}</p>
            <p className="home-about__text">{t("homepage_about_para2")}</p>
            <Link to="/about-us" className="home-about__link">
              Read more about us
              <ArrowForwardIcon fontSize="small" />
            </Link>
          </div>
        </section>
      </div>
    </AdminLayout>
  );
};

export default Homepage;
